import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import CTA from "@/components/CTA";
import Marquee from "@/components/Marquee";
import ClientLogos from "@/components/ClientLogos";
import { PILLARS, QUALIFICATIONS, MARQUEE_ITEMS } from "@/data/content";

function Story() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-2 gap-16">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
          <span className="w-10 h-px bg-[#C5A059]" /> What is Fox?
        </p>
        <h2 className="mt-6 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-zinc-100 leading-tight">
          A personal, hands-on approach to every project we deliver
        </h2>
      </Reveal>
      <div className="space-y-6">
        <Reveal delay={0.1}>
          <p className="text-base text-zinc-300 leading-relaxed font-light">
            FOX is a project management consultancy to the construction industry, spanning residential, commercial,
            event spaces, infrastructure and utilities. Through straightforward communication and uncomplicated
            processes, we help clients deliver construction and development projects with confidence and certainty.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="text-base text-zinc-300 leading-relaxed font-light">
            FOX is built on a genuine commitment to quality and being different from the rest — bringing a personal,
            hands-on approach to every project we deliver, with project locations across the UK &amp; Ireland.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="border-l-2 border-[#C5A059] pl-6 py-2">
            <p className="text-sm text-zinc-400 leading-relaxed font-light italic">
              "Our mission is clear, practical project management expertise to support clients through the
              ever-evolving construction environment — by being agile, smart and adaptive at every stage, with
              hands-on involvement."
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PillarsDetail() {
  return (
    <section className="border-y border-zinc-800/60 bg-void-2/30">
      <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C5A059]" /> The Fox philosophy
          </p>
        </Reveal>
        <div className="mt-14 space-y-0">
          {PILLARS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <div className="group grid md:grid-cols-12 gap-6 py-10 border-t border-zinc-800/60 last:border-b items-start">
                <span className="md:col-span-2 font-display text-4xl sm:text-5xl font-extrabold text-zinc-800 group-hover:text-[#C5A059] transition-colors duration-500">
                  {p.num}
                </span>
                <h3 className="md:col-span-3 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-zinc-100">
                  {p.title}
                </h3>
                <div className="md:col-span-7 space-y-4">
                  <p className="text-base text-zinc-300 font-light leading-relaxed">{p.text}</p>
                  <p className="text-sm text-zinc-500 leading-relaxed">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A059] block mb-1.5">
                      What it means for you
                    </span>
                    {p.benefit}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Standards() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 sm:py-32">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
          <span className="w-10 h-px bg-[#C5A059]" /> Chartered standards
        </p>
        <h2 className="mt-6 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-zinc-100 leading-tight">
          Qualified to the letter
        </h2>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800/60 border border-zinc-800/60">
        {QUALIFICATIONS.map((q, i) => (
          <Reveal key={q.abbr} delay={i * 0.1}>
            <div className="h-full bg-void p-8 hover:bg-void-2 transition-colors duration-500">
              <p className="font-display text-2xl font-extrabold text-zinc-50">{q.abbr}</p>
              <p className="mt-3 text-sm text-zinc-400 font-light">{q.name}</p>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">Est. {q.year}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-16 border border-zinc-800/80 bg-void-2/30 p-8 sm:p-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Client roster</p>
            <p className="mt-3 text-sm text-zinc-400 font-light max-w-xl leading-relaxed">
              Across a 22-year career, Carlo has built trusted and lasting relationships across the construction
              and development sector, working alongside leading organisations and industry partners.
            </p>
          </div>
          <Link
            to="/portfolio"
            data-testid="about-portfolio-link"
            className="group shrink-0 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300 hover:text-zinc-50 transition-colors"
          >
            See the work <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </Reveal>
      <ClientLogos testid="about-client-logo-strip" />
    </section>
  );
}

export default function About() {
  return (
    <>
      <PageHeader
        overline="About Fox"
        lines={["Precision in", "every instruction"]}
        intro="An independent construction project management consultancy built on agility, technical mastery and a genuine commitment to quality — different from the rest, by design."
        testid="about-headline"
      />
      <Story />
      <Marquee items={MARQUEE_ITEMS} />
      <PillarsDetail />
      <Standards />
      <CTA title="Work with a consultancy that fits how you work" />
    </>
  );
}
